import { useState } from 'react'

import { Borrow, Deposit, getBorrows, getDeposits } from './assets'
import { makeLiquidationDetails } from './makeLiquidationDetails'
import { makePositionSummary } from './position'
import { PositionSummary } from './types'
import { usePrefetchCampaigns } from './usePrefetchCampaigns'
import { makeWalletComposition, WalletCompositionInfo } from './wallet-composition'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { EModeCategoryId } from '@/features/sparklend/common/data/market-info/emode'
import { LiquidationDetails } from '@/features/sparklend/common/data/market-info/getLiquidationDetails'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { useSparkRewardsByReserve } from '@/features/sparklend/markets/logic/useSparkRewardsByReserve'

export interface UseMyPortfolioResults {
  positionSummary: PositionSummary
  deposits: Deposit[]
  borrows: Borrow[]
  walletComposition: WalletCompositionInfo
  guestMode: boolean
  eModeCategoryId: EModeCategoryId
  liquidationDetails?: LiquidationDetails
  openSandboxModal: () => void
  chainId: number
}

export function useMyPortfolio(): UseMyPortfolioResults {
  const { chainId } = usePageChainId()
  const { marketInfo } = useMarketInfo({ chainId })
  const sparkRewardsByReserve = useSparkRewardsByReserve({
    chainId,
    reserves: marketInfo.reserves,
  })
  const walletInfo = useMarketWalletInfo({ chainId })
  usePrefetchCampaigns()

  const [compositionWithDeposits, setCompositionWithDeposits] = useState(true)
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)
  const { openSandboxModal } = useSandbox()

  const deposits = getDeposits({
    marketInfo,
    walletInfo,
    nativeAssetInfo,
    sparkRewardsByReserve,
  })
  const borrows = getBorrows({ marketInfo, nativeAssetInfo, sparkRewardsByReserve })
  const positionSummary = makePositionSummary({ marketInfo })
  const walletComposition = makeWalletComposition({
    marketInfo,
    walletInfo,
    compositionWithDeposits,
    setCompositionWithDeposits,
    nativeAssetInfo,
    chainId: marketInfo.chainId,
  })

  const eModeCategoryId = (
    marketInfo.userConfiguration.eModeState.enabled ? marketInfo.userConfiguration.eModeState.category.id : 0
  ) as EModeCategoryId

  const liquidationDetails = makeLiquidationDetails(marketInfo)

  return {
    positionSummary,
    deposits,
    borrows,
    walletComposition,
    eModeCategoryId,
    guestMode: !walletInfo.isConnected,
    liquidationDetails,
    openSandboxModal,
    chainId,
  }
}
